import { useEffect, useRef, useState } from "react";
import BrowseServices from "./BrowseServices";
import Login from "./Login";
import Signup from "./Signup";
import ProviderDashboard from "./ProviderDashboard";
import AddService from "./AddService";
import MyServices from "./MyServices";
import ProviderProfilePage from "./ProviderProfilePage";
import MyPets from "./MyPets";
import ProviderBookings from "./ProviderBookings";
import OwnerBookings from "./OwnerBookings";
import OwnerPayments from "./OwnerPayments";
import ProviderEarnings from "./ProviderEarnings";
import ProviderPayouts from "./ProviderPayouts";
import ProviderReviews from "./ProviderReviews";
import AdminDashboard from "./AdminDashboard";
import OwnerDashboardHome from "./OwnerDashboardHome";
import NotificationsBell from "./NotificationsBell";
import LegalPage from "./LegalPage";
import SavedServices from "./SavedServices";
import SupportTickets from "./SupportTickets";
import ServiceLandingPage from "./ServiceLandingPage";
import InstantVetConsultation from "./InstantVetConsultation";
import SiteChrome from "./SiteChrome";
import PetAIAssistant from "./PetAIAssistant";
import OwnerMarketplace from "./OwnerMarketplace";
import ProviderMarketplace from "./ProviderMarketplace";
import MarketplaceCartPage from "./MarketplaceCartPage";
import ConsultationPage from "./pages/ConsultationPage";
import VetConsultationPopup from "./VetConsultationPopup";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import {
  buildConsultationHash,
  buildCategoryHash,
  buildLandingHash,
  buildPageHash,
  parseConsultationHash,
  parseCategoryHash,
  parseLandingHash,
  parsePageHash,
} from "./landingRoutes";

const ROUTE_STATE_KEY = "petaar_route_state_v1";
const OWNER_PAGES = new Set([
  "owner-dashboard",
  "instant-vet-consult",
  "my-pets",
  "owner-bookings",
  "owner-saved",
  "owner-payments",
  "owner-support",
  "owner-marketplace",
  "marketplace-cart",
  "consultation",
]);
const PROVIDER_PAGES = new Set([
  "provider-dashboard",
  "add-service",
  "my-services",
  "provider-bookings",
  "provider-earnings",
  "provider-payouts",
  "provider-reviews",
  "provider-support",
  "provider-marketplace",
  "consultation",
]);
const ADMIN_PAGES = new Set([
  "admin-dashboard",
  "admin-support",
  "consultation",
]);
const PUBLIC_PAGES = new Set([
  "browse",
  "marketplace",
  "marketplace-cart",
  "provider-profile",
  "landing",
  "category-landing",
  "login",
  "signup",
]);

const isLegalPage = (page) => String(page || "").startsWith("legal-");
const getDefaultPageForRole = (role) => {
  if (role === "provider") return "provider-dashboard";
  if (role === "admin") return "admin-dashboard";
  return "browse";
};
const isPageAllowedForRole = (page, role, isAuthenticated) => {
  const normalized = String(page || "").trim();
  if (!normalized) return false;
  if (isLegalPage(normalized)) return true;
  if (PUBLIC_PAGES.has(normalized)) return true;
  if (!isAuthenticated) return false;
  if (role === "owner") return OWNER_PAGES.has(normalized);
  if (role === "provider") return PROVIDER_PAGES.has(normalized);
  if (role === "admin") return ADMIN_PAGES.has(normalized);
  return false;
};
const normalizePageForRole = (page, role, isAuthenticated) => {
  const normalized = String(page || "").trim();
  if (isPageAllowedForRole(normalized, role, isAuthenticated)) {
    return normalized;
  }
  return getDefaultPageForRole(role);
};
const sanitizeSelectedService = (service) => {
  if (!service || typeof service !== "object") return null;
  return {
    id: service.id || "",
    title: service.title || "",
    category: service.category || "",
    city: service.city || "",
    price: Number(service.price || 0),
    providerEmail: service.providerEmail || "",
    providerId: service.providerId || "",
    availabilityNote: service.availabilityNote || "",
    imageUrls: Array.isArray(service.imageUrls) ? service.imageUrls : [],
    isFeatured: Boolean(service.isFeatured),
    featuredUntil: service.featuredUntil || "",
    languages: Array.isArray(service.languages) ? service.languages : [],
  };
};
const readRouteState = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ROUTE_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_) {
    return null;
  }
};
const writeRouteState = (payload) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ROUTE_STATE_KEY, JSON.stringify(payload));
  } catch (_) {}
};

export default function App() {
  const [page, setPage] = useState("browse");
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [providerStatus, setProviderStatus] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [landingContext, setLandingContext] = useState(null);
  const [categoryContext, setCategoryContext] = useState(null);
  const [postLoginPage, setPostLoginPage] = useState("browse");
  const [selectedPet, setSelectedPet] = useState(null);
  const [signupPrefillRole, setSignupPrefillRole] = useState("owner");
  const [aiServices, setAiServices] = useState([]);
  const [pendingAiIntent, setPendingAiIntent] = useState(null);
  const [consultationBookingId, setConsultationBookingId] = useState("");
  const [routeReady, setRouteReady] = useState(false);
  const pageRef = useRef("browse");
  const routeStateRef = useRef(null);
  const skipInitialRoutePersistRef = useRef(true);
  const skipInitialHashSyncRef = useRef(true);
  const getSelectedPetStorageKey = (uid) => `petaar_selected_pet_${uid}`;

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [page]);
  const navButtonClassName = (active = false, accent = false) => {
    if (accent) return "petaar-pill-tab is-accent";
    return active ? "petaar-pill-tab is-active" : "petaar-pill-tab";
  };
  const notificationButtonStyle = {
    minHeight: 40,
    padding: "10px 16px",
    borderRadius: 999,
    border: "1px solid rgba(26, 26, 219, 0.12)",
    background: "#fff",
    color: "#1a1adb",
    fontWeight: 700,
    boxShadow: "none",
    whiteSpace: "nowrap",
  };

  const loadRole = async (uid, nextPage = "") => {
    const userDoc = await getDoc(doc(db, "users", uid));
    const nextRole = userDoc.exists() ? userDoc.data().role : "owner";
    const nextProviderStatus = userDoc.exists() ? (userDoc.data().providerStatus || "") : "";
    setRole(nextRole);
    setProviderStatus(nextProviderStatus);
    const preferredPage = String(nextPage || "").trim() || getDefaultPageForRole(nextRole);
    setPage(normalizePageForRole(preferredPage, nextRole, true));
    return { role: nextRole, providerStatus: nextProviderStatus };
  };
  const openLandingPage = (context) => {
    if (!context?.city || !context?.category) return;
    setLandingContext({
      city: context.city,
      category: context.category,
    });
    setPage("landing");
    const nextHash = buildLandingHash(context);
    if (typeof window !== "undefined" && window.location.hash !== nextHash) {
      window.location.hash = nextHash;
    }
  };
  const openCategoryPage = (categoryName) => {
    const category = String(categoryName || "").trim();
    if (!category) return;
    setCategoryContext({ category });
    setPage("category-landing");
    const nextHash = buildCategoryHash(category);
    if (typeof window !== "undefined" && window.location.hash !== nextHash) {
      window.location.hash = nextHash;
    }
  };
  const closeLandingPage = () => {
    setPage("browse");
    if (typeof window !== "undefined" && window.location.hash.startsWith("#/services/")) {
      window.location.hash = "#/";
    }
  };
  const closeCategoryPage = () => {
    setPage("browse");
    if (typeof window !== "undefined" && window.location.hash.startsWith("#/category/")) {
      window.location.hash = "#/";
    }
  };
  const handleLogout = async () => {
    await auth.signOut();
    setUser(null);
    setRole(null);
    setProviderStatus("");
    setConsultationBookingId("");
    setSignupPrefillRole("owner");
    setSelectedPet(null);
    setPage("browse");
  };
  const renderOwnerTopNav = (activePage = "browse") => (
    <div className="petaar-role-nav">
      <div className="petaar-pill-tabs">
      <button onClick={handleHomeRoute} className={navButtonClassName(activePage === "browse")}>Browse</button>
      <button onClick={() => setPage("owner-dashboard")} className={navButtonClassName(activePage === "owner-dashboard")}>Dashboard</button>
      <button
        onClick={handleMarketplaceRoute}
        className={navButtonClassName(activePage === "owner-marketplace" || activePage === "marketplace")}
      >
        Marketplace
      </button>
      <button onClick={() => setPage("instant-vet-consult")} className={navButtonClassName(activePage === "instant-vet-consult")}>Consult Vet Now</button>
      <button onClick={() => setPage("my-pets")} className={navButtonClassName(activePage === "my-pets")}>My Pets</button>
      <button onClick={() => setPage("owner-saved")} className={navButtonClassName(activePage === "owner-saved")}>Saved</button>
      <button onClick={() => setPage("owner-bookings")} className={navButtonClassName(activePage === "owner-bookings")}>My Bookings</button>
      <button onClick={() => setPage("owner-payments")} className={navButtonClassName(activePage === "owner-payments")}>Payments</button>
      <button onClick={() => setPage("owner-support")} className={navButtonClassName(activePage === "owner-support")}>Support</button>
      <NotificationsBell user={user} buttonStyle={notificationButtonStyle} />
      <button onClick={handleLogout} className={navButtonClassName(false)}>Logout</button>
      </div>
    </div>
  );
  const renderProviderTopNav = (activePage = "browse") => (
    <div className="petaar-role-nav">
      <div className="petaar-pill-tabs">
      <button onClick={handleHomeRoute} className={navButtonClassName(activePage === "browse")}>Browse</button>
      <button onClick={() => setPage("provider-dashboard")} className={navButtonClassName(activePage === "provider-dashboard")}>Provider Dashboard</button>
      <button onClick={handleMarketplaceRoute} className={navButtonClassName(activePage === "provider-marketplace")}>Marketplace</button>
      <button onClick={() => setPage("my-services")} className={navButtonClassName(activePage === "my-services")}>My Services</button>
      <button onClick={() => setPage("provider-payouts")} className={navButtonClassName(activePage === "provider-payouts")}>Payouts</button>
      <button onClick={() => setPage("provider-support")} className={navButtonClassName(activePage === "provider-support")}>Support</button>
      <NotificationsBell user={user} buttonStyle={notificationButtonStyle} />
      <button onClick={handleLogout} className={navButtonClassName(false)}>Logout</button>
      </div>
    </div>
  );
  const renderAdminTopNav = (activePage = "browse") => (
    <div className="petaar-role-nav">
      <div className="petaar-pill-tabs">
      <button onClick={handleHomeRoute} className={navButtonClassName(activePage === "browse")}>Browse</button>
      <button onClick={() => setPage("admin-dashboard")} className={navButtonClassName(activePage === "admin-dashboard")}>Admin Dashboard</button>
      <button onClick={() => setPage("admin-support")} className={navButtonClassName(activePage === "admin-support")}>Support</button>
      <NotificationsBell user={user} buttonStyle={notificationButtonStyle} />
      <button onClick={handleLogout} className={navButtonClassName(false)}>Logout</button>
      </div>
    </div>
  );
  const handleConsultRoute = () => {
    if (!user) {
      setPostLoginPage("instant-vet-consult");
      setPage("login");
      return;
    }
    if (role === "provider") {
      setPage("provider-dashboard");
      return;
    }
    if (role === "admin") {
      setPage("admin-dashboard");
      return;
    }
    // Default signed-in users to owner consultation route even if role is temporarily unknown.
    setPage("instant-vet-consult");
  };
  const handleOwnerSignupRoute = () => {
    setSignupPrefillRole("owner");
    setPostLoginPage("browse");
    setPage("signup");
  };
  const handleProviderSignupRoute = async () => {
    if (!user) {
      setSignupPrefillRole("provider");
      setPostLoginPage("provider-dashboard");
      setPage("signup");
      return;
    }
    if (role === "provider") {
      setPage("provider-dashboard");
      return;
    }
    await auth.signOut();
    setUser(null);
    setRole(null);
    setProviderStatus("");
    setSelectedPet(null);
    setSignupPrefillRole("provider");
    setPage("signup");
  };
  const handleOpenDashboardRoute = () => {
    if (!user) {
      setPostLoginPage("browse");
      setPage("login");
      return;
    }
    if (role === "provider") {
      setPage("provider-dashboard");
      return;
    }
    if (role === "admin") {
      setPage("admin-dashboard");
      return;
    }
    setPage("owner-dashboard");
  };
  const handleMarketplaceRoute = () => {
    if (user && role === "provider") {
      setPage("provider-marketplace");
      return;
    }
    setPage("marketplace");
  };
  const goBrowseWithIntent = (intent = {}) => {
    setPendingAiIntent({
      ...intent,
      token: Date.now(),
    });
    if (pageRef.current !== "browse") {
      setPage("browse");
    }
  };
  const handleHomeRoute = () => {
    setPendingAiIntent(null);
    if (pageRef.current !== "browse") {
      setPage("browse");
    }
    setTimeout(() => {
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 70);
    if (typeof window !== "undefined" && (window.location.hash.startsWith("#/services/") || window.location.hash.startsWith("#/category/"))) {
      window.location.hash = "#/";
    }
  };
  const handleOpenServicesRoute = () => {
    goBrowseWithIntent({
      source: "top_nav",
      action: "services",
      focus: "services",
      resetFilters: true,
      clearCity: true,
      autoScroll: true,
    });
  };
  const handleOpenCategoryRoute = (categoryName = "") => {
    const category = String(categoryName || "").trim();
    if (!category) return;
    openCategoryPage(category);
  };
  const handleSelectCityRoute = (cityName = "") => {
    const city = String(cityName || "").trim();
    if (!city) {
      goBrowseWithIntent({
        source: "top_nav",
        action: "city_clear",
        clearCity: true,
        focus: "services",
        resetFilters: false,
        autoScroll: true,
      });
      return;
    }
    goBrowseWithIntent({
      source: "top_nav",
      action: "city",
      city,
      focus: "services",
      resetFilters: false,
      clearCity: false,
      autoScroll: true,
    });
  };
  const handleTopSearchRoute = (searchText = "") => {
    const query = String(searchText || "").trim();
    goBrowseWithIntent({
      source: "top_nav",
      action: "search",
      search: query,
      focus: "services",
      resetFilters: true,
      clearCity: true,
      autoScroll: true,
    });
  };
  const handleOpenGroomingRoute = () => {
    openCategoryPage("Grooming, Wellness & Spa");
  };
  const handleOpenBoardingRoute = () => {
    openCategoryPage("Boarding, Daycare & Sitting");
  };
  const handleAboutRoute = () => {
    setPage("browse");
    setTimeout(() => {
      const section = document.getElementById("site-footer-about");
      section?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };
  const handleContactRoute = () => {
    if (user && role === "provider") {
      setPage("provider-support");
      return;
    }
    if (user && role === "admin") {
      setPage("admin-support");
      return;
    }
    if (user && role === "owner") {
      setPage("owner-support");
      return;
    }
    setPage("browse");
    setTimeout(() => {
      const section = document.getElementById("site-footer-contact");
      section?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };
  const renderRoleTopNav = (activePage = "browse") => {
    if (user && role === "owner") return renderOwnerTopNav(activePage);
    if (user && role === "provider") return renderProviderTopNav(activePage);
    if (user && role === "admin") return renderAdminTopNav(activePage);
    return null;
  };
  const withSiteChrome = (content, activePage = "browse") => (
    <SiteChrome
      user={user}
      role={role}
      activePage={activePage}
      onBack={() => setPage("browse")}
      onHome={handleHomeRoute}
      onConsultVetNow={handleConsultRoute}
      onLogin={() => {
        setPostLoginPage("browse");
        setPage("login");
      }}
      onSignupOwner={handleOwnerSignupRoute}
      onSignupProvider={() => {
        void handleProviderSignupRoute();
      }}
      onOpenDashboard={handleOpenDashboardRoute}
      onOpenServices={handleOpenServicesRoute}
      onOpenMarketplace={handleMarketplaceRoute}
      onOpenGrooming={handleOpenGroomingRoute}
      onOpenBoarding={handleOpenBoardingRoute}
      onAboutUs={handleAboutRoute}
      onContactUs={handleContactRoute}
      onSearchServices={handleTopSearchRoute}
      onOpenCategory={handleOpenCategoryRoute}
      onSelectCity={handleSelectCityRoute}
      onMyServices={() => {
        if (role === "provider") setPage("my-services");
      }}
      onOpenLegal={(legalKey) => {
        if (legalKey === "about") setPage("legal-about");
        else if (legalKey === "terms") setPage("legal-terms");
        else if (legalKey === "privacy") setPage("legal-privacy");
        else if (legalKey === "refund") setPage("legal-refund");
        else if (legalKey === "support") setPage("legal-support");
        else if (legalKey === "help") setPage("legal-help");
        else if (legalKey === "medical") setPage("legal-medical");
        else if (legalKey === "contact") setPage("legal-contact");
      }}
    >
      {renderRoleTopNav(activePage)}
      {content}
      {user && role === "provider" ? (
        <VetConsultationPopup user={user} role={role} />
      ) : null}
      <PetAIAssistant
        services={aiServices}
        onOpenProvider={(service) => {
          setSelectedService(service);
          setPage("provider-profile");
        }}
        onApplyBookingIntent={handleAiBookingIntent}
        user={user}
        selectedPet={selectedPet}
        onHome={handleHomeRoute}
      />
    </SiteChrome>
  );

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    const publicServicesQuery = query(collection(db, "services"), where("isPublic", "==", true));
    const unsubscribe = onSnapshot(
      publicServicesQuery,
      (snapshot) => {
        const list = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        setAiServices(list);
      },
      () => {
        setAiServices([]);
      },
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const persisted = readRouteState();
    routeStateRef.current = {
      page: String(persisted?.page || "browse"),
      landingContext: persisted?.landingContext || null,
      categoryContext: persisted?.categoryContext || null,
      selectedService: persisted?.selectedService || null,
      consultationBookingId: String(persisted?.consultationBookingId || ""),
      postLoginPage: String(persisted?.postLoginPage || "browse"),
      signupPrefillRole:
        persisted?.signupPrefillRole === "provider" ? "provider" : "owner",
    };
    if (persisted?.landingContext) setLandingContext(persisted.landingContext);
    if (persisted?.categoryContext) setCategoryContext(persisted.categoryContext);
    if (persisted?.selectedService) setSelectedService(persisted.selectedService);
    if (persisted?.consultationBookingId) {
      setConsultationBookingId(String(persisted.consultationBookingId));
    }
    if (persisted?.postLoginPage) setPostLoginPage(String(persisted.postLoginPage));
    if (persisted?.signupPrefillRole === "provider" || persisted?.signupPrefillRole === "owner") {
      setSignupPrefillRole(persisted.signupPrefillRole);
    }
    if (!window.location.hash && persisted?.page) {
      setPage(String(persisted.page));
    }

    const applyHashRoute = () => {
      if (typeof window === "undefined") return;
      const parsedConsultation = parseConsultationHash(window.location.hash);
      if (parsedConsultation?.bookingId) {
        setConsultationBookingId(parsedConsultation.bookingId);
        setPage("consultation");
        return;
      }
      const parsed = parseLandingHash(window.location.hash);
      if (parsed) {
        setLandingContext(parsed);
        setPage("landing");
        return;
      }
      const parsedCategory = parseCategoryHash(window.location.hash);
      if (parsedCategory) {
        setCategoryContext(parsedCategory);
        setPage("category-landing");
        return;
      }
      const parsedPage = parsePageHash(window.location.hash);
      if (parsedPage?.page) {
        setPage(parsedPage.page);
      }
    };

    applyHashRoute();
    window.addEventListener("hashchange", applyHashRoute);

    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      const latestRoute = routeStateRef.current || readRouteState() || null;
      if (u) {
        try {
          const ownerIdentity = String(u.email || u.phoneNumber || "").trim();
          if (ownerIdentity) {
            localStorage.setItem("petaar_owner_identity", ownerIdentity);
          }
        } catch (_) {}
        try {
          const storedPetRaw = localStorage.getItem(getSelectedPetStorageKey(u.uid));
          if (storedPetRaw) {
            const parsed = JSON.parse(storedPetRaw);
            if (parsed && typeof parsed === "object") {
              setSelectedPet(parsed);
            }
          } else {
            setSelectedPet(null);
          }
        } catch (_) {
          setSelectedPet(null);
        }
        if (pageRef.current === "signup") {
          const userDoc = await getDoc(doc(db, "users", u.uid));
          const nextRole = userDoc.exists() ? userDoc.data().role : "owner";
          const nextProviderStatus = userDoc.exists() ? (userDoc.data().providerStatus || "") : "";
          setRole(nextRole);
          setProviderStatus(nextProviderStatus);
          setRouteReady(true);
          return;
        }

        const roleMeta = await loadRole(u.uid, String(latestRoute?.page || "browse"));
        const parsedConsultation = typeof window !== "undefined" ? parseConsultationHash(window.location.hash) : null;
        if (parsedConsultation?.bookingId) {
          setConsultationBookingId(parsedConsultation.bookingId);
          setPage(normalizePageForRole("consultation", roleMeta?.role || "owner", true));
          setRouteReady(true);
          return;
        }
        const parsed = typeof window !== "undefined" ? parseLandingHash(window.location.hash) : null;
        if (parsed) {
          setLandingContext(parsed);
          setPage("landing");
        } else {
          const parsedCategory = typeof window !== "undefined" ? parseCategoryHash(window.location.hash) : null;
          if (parsedCategory) {
            setCategoryContext(parsedCategory);
            setPage("category-landing");
          } else {
            const parsedPage = typeof window !== "undefined" ? parsePageHash(window.location.hash) : null;
            if (parsedPage?.page) {
              setPage(normalizePageForRole(parsedPage.page, roleMeta?.role || "owner", true));
            }
          }
        }
        setRouteReady(true);
      } else {
        try {
          localStorage.removeItem("petaar_owner_identity");
        } catch (_) {}
        setRole(null);
        setProviderStatus("");
        setSelectedPet(null);
        if (pageRef.current !== "signup") {
          const fallbackPage = normalizePageForRole(String(latestRoute?.page || "browse"), "", false);
          setPage(fallbackPage);
        }
        setRouteReady(true);
      }
    });

    return () => {
      unsub();
      window.removeEventListener("hashchange", applyHashRoute);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!routeReady) return;
    if (skipInitialHashSyncRef.current) {
      skipInitialHashSyncRef.current = false;
      return;
    }
    if (page === "landing" || page === "category-landing") return;
    const nextHash = page === "consultation" ?
      buildConsultationHash(consultationBookingId) :
      buildPageHash(page);
    if (window.location.hash === nextHash) return;
    window.history.replaceState(null, "", nextHash);
  }, [page, routeReady, consultationBookingId]);

  useEffect(() => {
    if (!user?.uid) return;
    const key = getSelectedPetStorageKey(user.uid);
    try {
      if (selectedPet && typeof selectedPet === "object") {
        localStorage.setItem(key, JSON.stringify(selectedPet));
      } else {
        localStorage.removeItem(key);
      }
    } catch (_) {}
  }, [selectedPet, user?.uid]);

  useEffect(() => {
    if (skipInitialRoutePersistRef.current) {
      skipInitialRoutePersistRef.current = false;
      return;
    }
    const payload = {
      page,
      landingContext,
      categoryContext,
      selectedService: sanitizeSelectedService(selectedService),
      consultationBookingId,
      postLoginPage,
      signupPrefillRole,
    };
    routeStateRef.current = payload;
    writeRouteState(payload);
  }, [page, landingContext, categoryContext, selectedService, consultationBookingId, postLoginPage, signupPrefillRole]);

  const handleAiBookingIntent = (intent = {}) => {
    goBrowseWithIntent(intent);
  };

  if (page === "landing" && landingContext?.city && landingContext?.category) {
    return withSiteChrome(
      <ServiceLandingPage
        city={landingContext.city}
        category={landingContext.category}
        user={user}
        selectedPet={selectedPet}
        onBack={closeLandingPage}
        onOpenProvider={(service) => {
          setSelectedService(service);
          setPage("provider-profile");
        }}
        onOpenLanding={openLandingPage}
        onLoginClick={(service) => {
          if (service) {
            setSelectedService(service);
            setPostLoginPage("provider-profile");
          } else {
            setPostLoginPage("landing");
          }
          setPage("login");
        }}
      />,
      "browse",
    );
  }

  if (page === "category-landing" && categoryContext?.category) {
    return withSiteChrome(
      <ServiceLandingPage
        city=""
        category={categoryContext.category}
        user={user}
        selectedPet={selectedPet}
        onBack={closeCategoryPage}
        onOpenProvider={(service) => {
          setSelectedService(service);
          setPage("provider-profile");
        }}
        onOpenLanding={openLandingPage}
        onLoginClick={(service) => {
          if (service) {
            setSelectedService(service);
            setPostLoginPage("provider-profile");
          } else {
            setPostLoginPage("category-landing");
          }
          setPage("login");
        }}
      />,
      "browse",
    );
  }

  if (!user && page === "login") {
    return withSiteChrome(
      <Login onLogin={async (u) => {
        setUser(u);
        await loadRole(u.uid, postLoginPage);
        setPostLoginPage("browse");
      }} goSignup={(selectedRole = "owner") => {
        setSignupPrefillRole(selectedRole === "provider" ? "provider" : "owner");
        setPage("signup");
      }} />,
      "browse",
    );
  }

  if (page === "signup") {
    return withSiteChrome(
      <Signup initialRole={signupPrefillRole} goLogin={() => setPage("login")} />,
      "browse",
    );
  }

  // CONSULTATION CHAT: Dedicated in-app consultation route for owners and vets.
  if (user && page === "consultation" && consultationBookingId) {
    return withSiteChrome(
      <ConsultationPage
        bookingId={consultationBookingId}
        user={user}
        userRole={role}
        onBack={() => {
          if (role === "provider") {
            setPage("provider-bookings");
            return;
          }
          if (role === "owner") {
            setPage("owner-bookings");
            return;
          }
          setPage("browse");
        }}
      />,
      role === "provider" ? "provider-dashboard" : role === "owner" ? "owner-bookings" : "browse",
    );
  }

  if (user && role === "provider" && page === "provider-dashboard") {
    return withSiteChrome(
      <ProviderDashboard
        email={user.email}
        providerId={user.uid}
        providerStatus={providerStatus}
        onAddService={() => setPage("add-service")}
        onMyServices={() => setPage("my-services")}
        onBookings={() => setPage("provider-bookings")}
        onEarnings={() => setPage("provider-earnings")}
        onPayouts={() => setPage("provider-payouts")}
        onReviews={() => setPage("provider-reviews")}
        onMarketplace={() => setPage("provider-marketplace")}
        onSupport={() => setPage("provider-support")}
        onLogout={handleLogout}
      />,
      "provider-dashboard",
    );
  }

  if (user && role === "provider" && page === "add-service") {
    return withSiteChrome(
      <AddService onBack={() => setPage("provider-dashboard")} />,
      "provider-dashboard",
    );
  }

  if (user && role === "provider" && page === "my-services") {
    return withSiteChrome(
      <MyServices onBack={() => setPage("provider-dashboard")} />,
      "my-services",
    );
  }

  if (user && role === "provider" && page === "provider-bookings") {
    return withSiteChrome(<ProviderBookings user={user} />, "provider-dashboard");
  }

  if (user && role === "provider" && page === "provider-earnings") {
    return withSiteChrome(<ProviderEarnings user={user} />, "provider-dashboard");
  }

  if (user && role === "provider" && page === "provider-payouts") {
    return withSiteChrome(<ProviderPayouts user={user} />, "provider-payouts");
  }

  if (user && role === "provider" && page === "provider-reviews") {
    return withSiteChrome(<ProviderReviews user={user} />, "provider-dashboard");
  }

  if (user && role === "provider" && page === "provider-support") {
    return withSiteChrome(
      <SupportTickets user={user} role="provider" />,
      "provider-support",
    );
  }

  if (user && role === "provider" && page === "provider-marketplace") {
    return withSiteChrome(
      <ProviderMarketplace providerId={user.uid} email={user.email} />,
      "provider-marketplace",
    );
  }

  if (user && role === "admin" && page === "admin-dashboard") {
    return withSiteChrome(
      <AdminDashboard
        onLogout={handleLogout}
      />,
      "admin-dashboard",
    );
  }

  if (user && role === "admin" && page === "admin-support") {
    return withSiteChrome(
      <SupportTickets user={user} role="admin" />,
      "admin-support",
    );
  }

  if (page === "provider-profile") {
    return withSiteChrome(
      <ProviderProfilePage
        service={selectedService}
        user={user}
        selectedPet={selectedPet}
        onBack={() => setPage("browse")}
        onLoginClick={(service) => {
          setSelectedService(service);
          setPostLoginPage("provider-profile");
          setPage("login");
        }}
      />,
      "browse",
    );
  }

  if (page === "legal-terms") {
    return withSiteChrome(<LegalPage pageKey="terms" onBack={() => setPage("browse")} />, "browse");
  }
  if (page === "legal-about") {
    return withSiteChrome(<LegalPage pageKey="about" onBack={() => setPage("browse")} />, "browse");
  }
  if (page === "legal-privacy") {
    return withSiteChrome(<LegalPage pageKey="privacy" onBack={() => setPage("browse")} />, "browse");
  }
  if (page === "legal-refund") {
    return withSiteChrome(<LegalPage pageKey="refund" onBack={() => setPage("browse")} />, "browse");
  }
  if (page === "legal-support") {
    return withSiteChrome(<LegalPage pageKey="support" onBack={() => setPage("browse")} />, "browse");
  }
  if (page === "legal-help") {
    return withSiteChrome(<LegalPage pageKey="help" onBack={() => setPage("browse")} />, "browse");
  }
  if (page === "legal-medical") {
    return withSiteChrome(<LegalPage pageKey="medical" onBack={() => setPage("browse")} />, "browse");
  }
  if (page === "legal-contact") {
    return withSiteChrome(<LegalPage pageKey="contact" onBack={() => setPage("browse")} />, "browse");
  }

  if (user && role === "owner" && page === "owner-dashboard") {
    return withSiteChrome(
      <OwnerDashboardHome
        user={user}
        onBrowse={() => setPage("browse")}
        onConsultVet={() => setPage("instant-vet-consult")}
        onMarketplace={() => setPage("owner-marketplace")}
        onMyPets={() => setPage("my-pets")}
        onSaved={() => setPage("owner-saved")}
        onMyBookings={() => setPage("owner-bookings")}
        onPayments={() => setPage("owner-payments")}
        onSupport={() => setPage("owner-support")}
        onLogout={handleLogout}
      />,
      "owner-dashboard",
    );
  }

  if (user && role === "owner" && page === "instant-vet-consult") {
    return withSiteChrome(
      <>
        <div style={{ padding: "clamp(12px, 3vw, 24px)", maxWidth: 1320, margin: "0 auto", width: "100%" }}>
          <InstantVetConsultation selectedPet={selectedPet} />
        </div>
      </>,
      "instant-vet-consult",
    );
  }

  if (user && role === "owner" && page === "my-pets") {
    return withSiteChrome(
      <MyPets
        user={user}
        selectedPetId={selectedPet?.id || ""}
        onSelectPet={setSelectedPet}
      />,
      "my-pets",
    );
  }

  if (user && role === "owner" && page === "owner-bookings") {
    return withSiteChrome(<OwnerBookings user={user} onHome={handleHomeRoute} />, "owner-bookings");
  }

  if (user && role === "owner" && page === "owner-saved") {
    return withSiteChrome(
      <SavedServices
        user={user}
        selectedPet={selectedPet}
        onOpenProvider={(service) => {
          setSelectedService(service);
          setPage("provider-profile");
        }}
      />,
      "owner-saved",
    );
  }

  if (user && role === "owner" && page === "owner-payments") {
    return withSiteChrome(<OwnerPayments user={user} />, "owner-payments");
  }

  if (user && role === "owner" && page === "owner-support") {
    return withSiteChrome(
      <SupportTickets user={user} role="owner" />,
      "owner-support",
    );
  }

  if (user && role === "owner" && page === "owner-marketplace") {
    return withSiteChrome(
      <OwnerMarketplace
        user={user}
        onOpenCartPage={() => setPage("marketplace-cart")}
      />,
      "owner-marketplace",
    );
  }

  if (page === "marketplace") {
    return withSiteChrome(
      <OwnerMarketplace
        user={user}
        onOpenCartPage={() => setPage("marketplace-cart")}
      />,
      "marketplace",
    );
  }

  if (page === "marketplace-cart") {
    return withSiteChrome(
      <MarketplaceCartPage
        user={user}
        onBackMarketplace={() => {
          if (user && role === "owner") {
            setPage("owner-marketplace");
            return;
          }
          setPage("marketplace");
        }}
        onLoginRequired={() => {
          setPostLoginPage("marketplace-cart");
          setPage("login");
        }}
      />,
      user && role === "owner" ? "owner-marketplace" : "marketplace",
    );
  }

  return withSiteChrome(
    <BrowseServices
      user={user}
      role={role}
      selectedPet={selectedPet}
      onHome={handleHomeRoute}
      onConsumeExternalAiBookingIntent={() => setPendingAiIntent(null)}
      hideLocalChrome
      hideLocalAi
      externalAiBookingIntent={pendingAiIntent}
      onOpenProvider={(service) => {
        setSelectedService(service);
        setPage("provider-profile");
      }}
      onOpenLanding={openLandingPage}
      onOpenCategoryPage={openCategoryPage}
      onOpenMarketplace={handleMarketplaceRoute}
      onSignupClick={handleOwnerSignupRoute}
      onConsultVetNow={handleConsultRoute}
      onVetJoinClick={handleProviderSignupRoute}
      onOpenLegal={(legalKey) => {
        if (legalKey === "about") setPage("legal-about");
        else if (legalKey === "terms") setPage("legal-terms");
        else if (legalKey === "privacy") setPage("legal-privacy");
        else if (legalKey === "refund") setPage("legal-refund");
        else if (legalKey === "support") setPage("legal-support");
        else if (legalKey === "help") setPage("legal-help");
        else if (legalKey === "medical") setPage("legal-medical");
        else if (legalKey === "contact") setPage("legal-contact");
      }}
      onLoginClick={(service) => {
        if (service) {
          setSelectedService(service);
          setPostLoginPage("provider-profile");
        } else {
          setPostLoginPage("browse");
        }
        setPage("login");
      }}
    />,
    "browse",
  );
}
